/**
 * AdminCatalogoMejorado
 * Pantalla mejorada para gestionar Áreas, Partidas y Recursos con tablas profesionales
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import { areaService, partidaService, recursoService } from '../services/catalogoService';
import AreaModal from '../components/AreaModal';
import PartidaModal from '../components/PartidaModal';
import RecursoModal from '../components/RecursoModal';
import Loading from '../components/Loading';
import './AdminCatalogoMejorado.css';

const AdminCatalogoMejorado = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('areas');

  // Areas state
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [areaModalMode, setAreaModalMode] = useState('create');
  const [selectedArea, setSelectedArea] = useState(null);
  const [areasSearch, setAreasSearch] = useState('');

  // Partidas state
  const [partidas, setPartidas] = useState([]);
  const [partidasLoading, setPartidasLoading] = useState(false);
  const [partidaModalOpen, setPartidaModalOpen] = useState(false);
  const [partidaModalMode, setPartidaModalMode] = useState('create');
  const [selectedPartida, setSelectedPartida] = useState(null);
  const [partidasSearch, setPartidasSearch] = useState('');

  // Recursos state
  const [recursos, setRecursos] = useState([]);
  const [recursosLoading, setRecursosLoading] = useState(false);
  const [recursoModalOpen, setRecursoModalOpen] = useState(false);
  const [recursoModalMode, setRecursoModalMode] = useState('create');
  const [selectedRecurso, setSelectedRecurso] = useState(null);
  const [recursosSearch, setRecursosSearch] = useState('');

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'areas') {
      cargarAreas();
    } else if (activeTab === 'partidas') {
      cargarPartidas();
    } else if (activeTab === 'recursos') {
      cargarRecursos();
    }
  }, [activeTab]);

  // ===========================================
  // AREAS CRUD
  // ===========================================

  const cargarAreas = async () => {
    setAreasLoading(true);
    try {
      const response = await areaService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAreas(data);
    } catch (error) {
      console.error('Error al cargar áreas:', error);
      toast.error('Error al cargar áreas');
    } finally {
      setAreasLoading(false);
    }
  };

  const abrirAreaModal = (mode, area = null) => {
    setAreaModalMode(mode);
    setSelectedArea(area);
    setAreaModalOpen(true);
  };

  const cerrarAreaModal = () => {
    setAreaModalOpen(false);
    setSelectedArea(null);
  };

  const eliminarArea = async (areaId, areaNombre) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el área "${areaNombre}"?`)) {
      return;
    }

    try {
      await areaService.delete(areaId);
      toast.success('Área eliminada exitosamente');
      cargarAreas();
    } catch (error) {
      console.error('Error al eliminar área:', error);
      toast.error('Error al eliminar área');
    }
  };

  // ===========================================
  // PARTIDAS CRUD
  // ===========================================

  const cargarPartidas = async () => {
    setPartidasLoading(true);
    try {
      const response = await partidaService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPartidas(data);
    } catch (error) {
      console.error('Error al cargar partidas:', error);
      toast.error('Error al cargar partidas');
    } finally {
      setPartidasLoading(false);
    }
  };

  const abrirPartidaModal = (mode, partida = null) => {
    setPartidaModalMode(mode);
    setSelectedPartida(partida);
    setPartidaModalOpen(true);
  };

  const cerrarPartidaModal = () => {
    setPartidaModalOpen(false);
    setSelectedPartida(null);
  };

  const eliminarPartida = async (partidaId, partidaNombre) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la partida "${partidaNombre}"?`)) {
      return;
    }

    try {
      await partidaService.delete(partidaId);
      toast.success('Partida eliminada exitosamente');
      cargarPartidas();
    } catch (error) {
      console.error('Error al eliminar partida:', error);
      toast.error('Error al eliminar partida');
    }
  };

  // ===========================================
  // RECURSOS CRUD
  // ===========================================

  const cargarRecursos = async () => {
    setRecursosLoading(true);
    try {
      const response = await recursoService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setRecursos(data);
    } catch (error) {
      console.error('Error al cargar recursos:', error);
      toast.error('Error al cargar recursos');
    } finally {
      setRecursosLoading(false);
    }
  };

  const abrirRecursoModal = (mode, recurso = null) => {
    setRecursoModalMode(mode);
    setSelectedRecurso(recurso);
    setRecursoModalOpen(true);
  };

  const cerrarRecursoModal = () => {
    setRecursoModalOpen(false);
    setSelectedRecurso(null);
  };

  const eliminarRecurso = async (recursoId, recursoNombre) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el recurso "${recursoNombre}"?`)) {
      return;
    }

    try {
      await recursoService.delete(recursoId);
      toast.success('Recurso eliminado exitosamente');
      cargarRecursos();
    } catch (error) {
      console.error('Error al eliminar recurso:', error);
      toast.error('Error al eliminar recurso');
    }
  };

  // ===========================================
  // UTILIDADES
  // ===========================================

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  // Filtrado de datos
  const areasFiltered = areas.filter(area =>
    area.nombre?.toLowerCase().includes(areasSearch.toLowerCase()) ||
    area.descripcion?.toLowerCase().includes(areasSearch.toLowerCase())
  );

  const partidasFiltered = partidas.filter(partida =>
    partida.nombre?.toLowerCase().includes(partidasSearch.toLowerCase()) ||
    partida.descripcion?.toLowerCase().includes(partidasSearch.toLowerCase())
  );

  const recursosFiltered = recursos.filter(recurso =>
    recurso.nombre?.toLowerCase().includes(recursosSearch.toLowerCase()) ||
    recurso.descripcion?.toLowerCase().includes(recursosSearch.toLowerCase())
  );

  // ===========================================
  // RENDER
  // ===========================================

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="admin-catalogo-mejorado-container">
      <div className="page-header">
        <div>
          <h1>Catálogo Maestro</h1>
          <p className="page-subtitle">
            Gestiona la estructura jerárquica de Áreas, Partidas y Recursos
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'areas' ? 'active' : ''}`}
          onClick={() => setActiveTab('areas')}
        >
          Áreas
        </button>
        <button
          className={`tab ${activeTab === 'partidas' ? 'active' : ''}`}
          onClick={() => setActiveTab('partidas')}
        >
          Partidas
        </button>
        <button
          className={`tab ${activeTab === 'recursos' ? 'active' : ''}`}
          onClick={() => setActiveTab('recursos')}
        >
          Recursos
        </button>
      </div>

      {/* Content */}
      <div className="tab-content">
        {/* ÁREAS TAB */}
        {activeTab === 'areas' && (
          <div className="tab-panel">
            <div className="panel-header">
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar áreas..."
                  value={areasSearch}
                  onChange={(e) => setAreasSearch(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={() => abrirAreaModal('create')}
              >
                <FiPlus /> Nueva Área
              </button>
            </div>

            {areasLoading ? (
              <Loading fullScreen={false} mensaje="Cargando áreas" />
            ) : areasFiltered.length === 0 ? (
              <div className="empty-state">
                <p>No hay áreas disponibles</p>
                <button
                  className="btn btn-primary"
                  onClick={() => abrirAreaModal('create')}
                >
                  <FiPlus /> Crear primera área
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Costo Total</th>
                      <th>Venta Total</th>
                      <th>Margen %</th>
                      <th className="actions-column">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areasFiltered.map(area => (
                      <tr key={area.id}>
                        <td className="font-semibold">{area.nombre}</td>
                        <td className="description-cell">
                          {area.descripcion || '-'}
                        </td>
                        <td>{formatCurrency(area.valor_unitario_costo)}</td>
                        <td>{formatCurrency(area.valor_unitario_venta)}</td>
                        <td>{(parseFloat(area.markup_promedio) || 0).toFixed(2)}%</td>
                        <td className="actions-cell">
                          <button
                            className="btn-icon edit"
                            onClick={() => abrirAreaModal('edit', area)}
                            title="Editar"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn-icon delete"
                            onClick={() => eliminarArea(area.id, area.nombre)}
                            title="Eliminar"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* PARTIDAS TAB */}
        {activeTab === 'partidas' && (
          <div className="tab-panel">
            <div className="panel-header">
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar partidas..."
                  value={partidasSearch}
                  onChange={(e) => setPartidasSearch(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={() => abrirPartidaModal('create')}
              >
                <FiPlus /> Nueva Partida
              </button>
            </div>

            {partidasLoading ? (
              <Loading fullScreen={false} mensaje="Cargando partidas" />
            ) : partidasFiltered.length === 0 ? (
              <div className="empty-state">
                <p>No hay partidas disponibles</p>
                <button
                  className="btn btn-primary"
                  onClick={() => abrirPartidaModal('create')}
                >
                  <FiPlus /> Crear primera partida
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Unidad</th>
                      <th>Costo Total</th>
                      <th>Venta Total</th>
                      <th>Margen %</th>
                      <th className="actions-column">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partidasFiltered.map(partida => (
                      <tr key={partida.id}>
                        <td className="font-semibold">{partida.nombre}</td>
                        <td className="description-cell">
                          {partida.descripcion || '-'}
                        </td>
                        <td>{partida.unidad || '-'}</td>
                        <td>{formatCurrency(partida.valor_unitario_costo)}</td>
                        <td>{formatCurrency(partida.valor_unitario_venta)}</td>
                        <td>{(parseFloat(partida.markup_promedio) || 0).toFixed(2)}%</td>
                        <td className="actions-cell">
                          <button
                            className="btn-icon edit"
                            onClick={() => abrirPartidaModal('edit', partida)}
                            title="Editar"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn-icon delete"
                            onClick={() => eliminarPartida(partida.id, partida.nombre)}
                            title="Eliminar"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* RECURSOS TAB */}
        {activeTab === 'recursos' && (
          <div className="tab-panel">
            <div className="panel-header">
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar recursos..."
                  value={recursosSearch}
                  onChange={(e) => setRecursosSearch(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={() => abrirRecursoModal('create')}
              >
                <FiPlus /> Nuevo Recurso
              </button>
            </div>

            {recursosLoading ? (
              <Loading fullScreen={false} mensaje="Cargando recursos" />
            ) : recursosFiltered.length === 0 ? (
              <div className="empty-state">
                <p>No hay recursos disponibles</p>
                <button
                  className="btn btn-primary"
                  onClick={() => abrirRecursoModal('create')}
                >
                  <FiPlus /> Crear primer recurso
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Área</th>
                      <th>Partida</th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Cantidad</th>
                      <th>Unidad</th>
                      <th>Precio Costo</th>
                      <th>Precio Venta</th>
                      <th>Margen %</th>
                      <th>Subtotal</th>
                      <th className="actions-column">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recursosFiltered.map(recurso => (
                      <tr key={recurso.id}>
                        <td>{recurso.area_nombre || '-'}</td>
                        <td>{recurso.partida_nombre || '-'}</td>
                        <td className="font-semibold">{recurso.nombre}</td>
                        <td className="description-cell">
                          {recurso.descripcion || '-'}
                        </td>
                        <td>{recurso.cantidad_presupuesto}</td>
                        <td>{recurso.unidad}</td>
                        <td>{formatCurrency(recurso.precio_unitario_costo)}</td>
                        <td>{formatCurrency(recurso.precio_unitario_venta)}</td>
                        <td>{(parseFloat(recurso.markup) || 0).toFixed(2)}%</td>
                        <td className="font-semibold">
                          {formatCurrency(recurso.subtotal)}
                        </td>
                        <td className="actions-cell">
                          <button
                            className="btn-icon edit"
                            onClick={() => abrirRecursoModal('edit', recurso)}
                            title="Editar"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn-icon delete"
                            onClick={() => eliminarRecurso(recurso.id, recurso.nombre)}
                            title="Eliminar"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modales */}
      <AreaModal
        isOpen={areaModalOpen}
        mode={areaModalMode}
        area={selectedArea}
        onClose={cerrarAreaModal}
        onSuccess={cargarAreas}
      />

      <PartidaModal
        isOpen={partidaModalOpen}
        mode={partidaModalMode}
        partida={selectedPartida}
        onClose={cerrarPartidaModal}
        onSuccess={cargarPartidas}
      />

      <RecursoModal
        isOpen={recursoModalOpen}
        mode={recursoModalMode}
        recurso={selectedRecurso}
        onClose={cerrarRecursoModal}
        onSuccess={cargarRecursos}
      />
    </div>
  );
};

export default AdminCatalogoMejorado;
